/**
 * Support sessions. TutorLoop staff sign in to a workspace through a
 * 30-minute support link; their access token carries a `support_by` claim
 * (the staff email) and expires at the end of the session. The payload is
 * decoded locally, without verification: it only drives the banner, and the
 * server still checks the token on every request.
 */

import { tokenStorage } from "./tokens";

export interface SupportSession {
  /** Staff email from the `support_by` claim. */
  by: string;
  /** Token expiry, in milliseconds since the epoch. */
  expiresAt: number;
}

function decodePayload(token: string): Record<string, unknown> | null {
  const part = token.split(".")[1];
  if (!part) return null;
  try {
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      Array.from(atob(b64.padEnd(Math.ceil(b64.length / 4) * 4, "=")), (c) => `%${c.charCodeAt(0).toString(16).padStart(2, "0")}`).join(""),
    );
    const parsed: unknown = JSON.parse(json);
    return typeof parsed === "object" && parsed !== null ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** The current support session, or null for an ordinary sign in. */
export function readSupportSession(): SupportSession | null {
  const access = tokenStorage.getAccess();
  if (!access) return null;
  const claims = decodePayload(access);
  if (!claims) return null;
  const by = claims.support_by;
  const exp = claims.exp;
  if (typeof by !== "string" || by === "" || typeof exp !== "number") return null;
  return { by, expiresAt: exp * 1000 };
}
